/**
 * Meetings Store — manages meeting list, types, and CRUD.
 * Domain: Meetings & Scheduling
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

export const useMeetingsStore = defineStore('meetings', () => {
  // ─── State ───────────────────────────────────────────────────────────────
  const items        = ref([])
  const meetingTypes = ref([])
  const loading      = ref(false)
  const searchQuery  = ref('')
  const statusFilter = ref('')
  const typeFilter   = ref('')

  // ─── Getters ─────────────────────────────────────────────────────────────
  const filtered = computed(() => {
    let list = items.value
    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase()
      list = list.filter(m => m.title?.toLowerCase().includes(q) || m.location?.toLowerCase().includes(q))
    }
    if (statusFilter.value) list = list.filter(m => m.status === statusFilter.value)
    if (typeFilter.value)   list = list.filter(m => m.meeting_type?.name === typeFilter.value)
    return list
  })

  const upcoming = computed(() =>
    items.value.filter(m => m.status === 'scheduled' || m.status === 'upcoming')
  )

  // ─── Actions ─────────────────────────────────────────────────────────────
  const fetchAll = async (params = {}) => {
    loading.value = true
    try {
      const response = await api.get('/meetings', { params })
      items.value = response.data.meetings || []
      return items.value
    } catch (error) {
      console.error('Failed to fetch meetings:', error)
      return []
    } finally {
      loading.value = false
    }
  }

  const fetchById = async (id) => {
    try {
      const response = await api.get(`/meetings/${id}`)
      return response.data
    } catch (error) {
      console.error(`Failed to fetch meeting ${id}:`, error)
      throw error
    }
  }

  const fetchTypes = async () => {
    try {
      const response = await api.get('/meetings/types')
      meetingTypes.value = response.data.meeting_types || []
      return meetingTypes.value
    } catch (error) {
      console.error('Failed to fetch meeting types:', error)
      return []
    }
  }

  const create = async (data) => {
    try {
      const response = await api.post('/meetings', data)
      const newItem = response.data.meeting
      items.value.unshift(newItem)
      return { success: true, meeting: newItem }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to create meeting' }
    }
  }

  const update = async (id, data) => {
    try {
      const response = await api.put(`/meetings/${id}`, data)
      const updated = response.data.meeting
      const idx = items.value.findIndex(m => m.id === id)
      if (idx !== -1) items.value[idx] = updated
      return { success: true, meeting: updated }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update meeting' }
    }
  }

  const remove = async (id) => {
    try {
      await api.delete(`/meetings/${id}`)
      items.value = items.value.filter(m => m.id !== id)
      return { success: true }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to delete meeting' }
    }
  }

  const updateStatus = async (id, status, notes = '') => {
    try {
      const response = await api.patch(`/meetings/${id}/status`, { status, notes })
      const updated = response.data.meeting
      const idx = items.value.findIndex(m => m.id === id)
      if (idx !== -1) items.value[idx] = updated
      return { success: true, meeting: updated }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update meeting status' }
    }
  }

  return {
    // State
    items, meetingTypes, loading, searchQuery, statusFilter, typeFilter,
    // Getters
    filtered, upcoming,
    // Actions
    fetchAll, fetchById, fetchTypes, create, update, remove, updateStatus
  }
})
